import { handleErrorAndAlert } from '@zyno-io/vue-foundation';

import { MobileBridge } from '@/modules/mobile/helper/bridge';
import { hasRouteAccess } from '@/shared/helpers/permissions.helpers';
import { Auth } from '@/shared/services/auth.service';

// The standalone ZynoTalk mobile app. We deep-link via its custom URL scheme (a universal link
// navigated from inside our own pages wouldn't leave the browser/webview) and fall back to the store
// listing when it isn't installed. Only the production build is in the stores.
const ZynoTalkApp = {
    storeUrl: {
        ios: 'https://apps.apple.com/us/app/zynotalk/id6478135154',
        android: 'https://play.google.com/store/apps/details?id=app.zyno.talk'
    }
} as const;

// Each ZynoTalk build registers its own scheme, so this SPA build targets its matching app:
// zynotalk-mobile's .gitlab-ci.yml `URL_SCHEME` for alpha (`_dev`) and production, app.json's `-dev`
// for local builds.
const ZynoTalkSchemes: Partial<Record<string, string>> = {
    production: 'app.zyno.talk',
    alpha: 'app.zyno.talk-alpha'
};

/** The URL scheme (no `://`) of the ZynoTalk build paired with this SPA environment. */
export function zynoTalkScheme(env: string = import.meta.env.VITE_APP_ENV): string {
    return ZynoTalkSchemes[env] ?? 'app.zyno.talk-dev';
}

/**
 * The tenant every ZynoTalk link names (`tenantId`), so ZynoTalk opens on the tenant the user is in
 * here. Null inside a ZynoSuite mobile build that doesn't report its tenant: there `Auth.tenantId` is
 * the webview's own stored pick, not the tenant the app is showing. Unnamed, ZynoTalk stays put.
 */
function zynoTalkTenantId(): string | null {
    if (MobileBridge.isWithinZynoSuiteMobile && !MobileBridge.config.value?.tenantId) return null;
    return Auth.tenantId.value;
}

const withTenant = (query: string) => {
    const tenantId = zynoTalkTenantId();
    return tenantId ? `${query}${query ? '&' : '?'}tenantId=${encodeURIComponent(tenantId)}` : query;
};

/**
 * ZynoTalk's "dial this number" deep link: opens the app's dialer with the number prefilled, not
 * dialed. Contract shared with zynotalk-mobile (`<scheme>://dial?number=…&tenantId=…`).
 */
export function zynoTalkDialUrl(phoneNumber: string): string {
    return `${zynoTalkScheme()}://dial${withTenant(`?number=${encodeURIComponent(phoneNumber)}`)}`;
}

/** ZynoTalk's bare launch link: brings the app forward on the tenant (`<scheme>://?tenantId=…`). */
export function zynoTalkLaunchUrl(): string {
    return `${zynoTalkScheme()}://${withTenant('')}`;
}

/**
 * ZynoTalk's deep link for a Talk web route: `/talk/sms?to=…` → `<scheme>://talk/sms?to=…&tenantId=…`,
 * the same path and query ZynoTalk takes from its `https://zyno.app/talk/*` links. The fragment (the
 * web's `#tid:` tenant marker) gives way to the `tenantId` query.
 */
export function zynoTalkRouteUrl(fullPath: string): string {
    const url = new URL(fullPath, 'https://zyno.app');
    if (url.searchParams.has('tenantId')) url.searchParams.delete('tenantId');
    return `${zynoTalkScheme()}://${url.pathname.replace(/^\//, '')}${withTenant(url.search)}`;
}

/**
 * Which app store a phone-browser user should be sent to for ZynoTalk, or `null` when there is no
 * store hand-off to make: inside the native app (it has a bridge that queries install state) and on
 * desktop (no phone store, and the desktop Talk web screens work fine).
 */
export function talkBrowserStoreOS(): 'ios' | 'android' | null {
    if (MobileBridge.isWithinZynoSuiteMobile) return null;
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    return null;
}

/**
 * Whether to surface the ZynoTalk launcher tile at all — Talk has no usable mobile web surface, so
 * for Talk-entitled tenants we offer a hand-off to the standalone app (native app) or its store
 * (phone browser). Desktop keeps the normal routing tile, so this is false there.
 */
export function showZynoTalkLauncher(): boolean {
    return hasRouteAccess('/talk') && (MobileBridge.isWithinZynoSuiteMobile || talkBrowserStoreOS() !== null);
}

/**
 * Launch ZynoTalk from the launcher tile. In the native app this hands off through the bridge (deep
 * link, store fallback when not installed). In a phone browser it uses the deep-link-then-timeout
 * trick below.
 */
export function launchZynoTalk(): Promise<void> {
    return openZynoTalk(zynoTalkLaunchUrl());
}

/** Open ZynoTalk's dialer with the number prefilled — same hand-off (and store fallback) as the tile. */
export function dialViaZynoTalk(phoneNumber: string): Promise<void> {
    return openZynoTalk(zynoTalkDialUrl(phoneNumber));
}

/** Open ZynoTalk at the screen matching a Talk web route (see `zynoTalkRouteUrl`). */
export function openZynoTalkRoute(fullPath: string): Promise<void> {
    return openZynoTalk(zynoTalkRouteUrl(fullPath));
}

async function openZynoTalk(url: string): Promise<void> {
    if (MobileBridge.isWithinZynoSuiteMobile) {
        const platform = MobileBridge.config.value?.platform ?? 'ios';
        try {
            await MobileBridge.openExternalApp(url, ZynoTalkApp.storeUrl[platform]);
        } catch (err) {
            handleErrorAndAlert(err);
        }
        return;
    }

    const os = talkBrowserStoreOS();
    if (os) openAppOrStore(os, url);
}

/**
 * Best-effort "open the app, else its store" for a phone browser. We navigate to ZynoTalk's custom
 * URL scheme; if the app is installed it opens and the tab is backgrounded. We watch for that
 * background (visibilitychange → hidden, or pagehide) and, if it hasn't happened after a short grace
 * period, assume the app isn't installed and redirect to the store. We deliberately do NOT treat
 * window `blur` as "app opened" — iOS fires blur for its own scheme-permission / "Cannot Open Page"
 * dialogs, which would wrongly suppress the store fallback.
 *
 * Custom schemes can't be probed reliably on every browser (iOS may surface a "Cannot Open Page"
 * prompt when the app is absent), so this is a graceful fallback, not a guarantee.
 */
function openAppOrStore(os: 'ios' | 'android', url: string): void {
    let backgrounded = false;
    const onVisibility = () => {
        if (document.visibilityState === 'hidden') backgrounded = true;
    };
    const onPageHide = () => {
        backgrounded = true;
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);

    // Attempt the deep link. If ZynoTalk is installed it opens and the tab backgrounds.
    window.location.href = url;

    window.setTimeout(() => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pagehide', onPageHide);
        // Still here and foregrounded → the app didn't open; send them to the store instead.
        if (!backgrounded && document.visibilityState === 'visible') {
            window.location.href = ZynoTalkApp.storeUrl[os];
        }
    }, 1000);
}
